<mat-card>
  @if (isLoadingData) {
    <div class="year-change-overlay">
      <mat-spinner diameter="36"></mat-spinner>
      <span>Loading {{ performanceYear }} questionnaire...</span>
    </div>
  }
  <mat-card-content>
    <div id="top" class="questions-header">
      @if (showYearSelector) {
        <mat-form-field appearance="outline" class="year-selector read-only-allowed">
          <mat-label>Performance Year</mat-label>
          <mat-select [value]="performanceYear" (selectionChange)="onYearChange($event.value)" [disabled]="isLoadingData">
            @for (year of availableYears; track year) {
              <mat-option [value]="year">{{ year }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      } @else {
        <p class="performance-year-label">
          Performance Year: <strong>{{ currentPacket()?.performanceYear || performanceYear }}</strong>
        </p>
      }
      <div class="header-content">
        <h2 class="section-title">
          Merit-based Incentive Payment System (MIPS) Questionnaire
        </h2>

        @if (isSuperAdmin$) {
        <div class="export-actions">
          <button
            mat-raised-button
            color="primary"
            (click)="onExportQuestionnaires()"
            [disabled]="isExporting"
            matTooltip="Export all MIPS questionnaires from associated organizations"
            class="export-button"
          >
            <mat-icon>download</mat-icon>

            @if (isExporting) {
            <span>Exporting...</span>
            } @else {
            <span>Export All Questionnaires</span>
            }
          </button>
        </div>
        }
      </div>
    </div>

    <mat-divider></mat-divider>

    <div class="disclaimer warning">
      <mat-icon class="disclaimer-icon">info</mat-icon>
      <p class="disclaimer-text">
        Disclaimer: Responses provided below are applied to your selected group.
        Re-weighting may occur for all categories based on your selection. Score
        provided in the dashboard is an estimate and should not be considered as
        final.
      </p>
    </div>

    <div class="disclaimer-container">
      <div class="disclaimer note">
        <p class="disclaimer-text">
          Note: As per CMS the Small Practice reweighting takes precedence over
          the other reweighting options for PI if they are selected.
        </p>
      </div>
    </div>

    @if (isSelectedYearClosed) {
      <div class="error-banner">
        <mat-icon>lock</mat-icon>
        <span>The {{ performanceYear }} submission window has closed. This questionnaire is read-only. Please select {{ performanceYear + 1 }} to make edits.</span>
      </div>
    }

    <form [formGroup]="mipsForm">
      <div id="all-inputs" class="input-group" [class.closed]="isSelectedYearClosed">
        <div class="form-group full-width">
          <div class="question-container">
            <label class="question-label">
              * Are you required to report MIPS?
            </label>

            <mat-radio-group
              formControlName="requiredToReport"
              class="inline-radio-group"
            >
              <mat-radio-button value="yes">Yes</mat-radio-button>
              <mat-radio-button value="no">No</mat-radio-button>
            </mat-radio-group>

            @if (isFieldInvalid('requiredToReport')) {
            <mat-error class="error-message">
              This field is required
            </mat-error>
            }
          </div>
        </div>

        <div id="break"></div>

        <div class="form-group full-width">
          <div class="question-container">
            <label class="question-label">
              * Will you report Traditional MIPS, MIPS Value Pathways (MVP), or
              Both?
            </label>

            <mat-radio-group
              formControlName="reportingType"
              class="inline-radio-group"
            >
              <mat-radio-button value="traditional">
                Traditional MIPS
              </mat-radio-button>
              <mat-radio-button value="mvp">
                MIPS Value Pathways (MVP)
              </mat-radio-button>
              <mat-radio-button value="both">Both</mat-radio-button>
            </mat-radio-group>

            @if (isFieldInvalid('reportingType')) {
            <mat-error class="error-message">
              This field is required
            </mat-error>
            }
          </div>
        </div>

        <div id="break"></div>

        <div class="form-group full-width">
          <div class="question-container">
            <label class="question-label">
              * What level of reporting will you submit? (Select all that apply)
            </label>

            <div class="checkbox-container" formGroupName="reportingLevel">
              <mat-checkbox formControlName="individual">
                Individual
              </mat-checkbox>
              <mat-checkbox formControlName="group"> Group </mat-checkbox>
              <mat-checkbox formControlName="subgroup"> Subgroup </mat-checkbox>
            </div>

            @if (
            mipsForm.get('reportingLevel')?.invalid && (
            mipsForm.get('reportingLevel')?.dirty ||
            mipsForm.get('reportingLevel')?.touched ) ) {
            <mat-error class="error-message">
              Please select at least one reporting level
            </mat-error>
            }
          </div>
        </div>

        <div id="break"></div>

        <div class="form-group full-width">
          <div class="question-container">
            <label class="question-label">
              * Does your practice qualify for a special status?
              <a
                href="https://qpp.cms.gov/eligibility-participation/eligibility/special-statuses"
                target="_blank"
                class="link"
              >For more information, you can view your practice's status here.</a>
            </label>

            <mat-radio-group
              formControlName="specialStatus"
              class="inline-radio-group"
            >
              <mat-radio-button value="yes">Yes</mat-radio-button>
              <mat-radio-button value="no">No</mat-radio-button>
            </mat-radio-group>

            @if (isFieldInvalid('specialStatus')) {
            <mat-error class="error-message">
              This field is required
            </mat-error>
            }
          </div>
        </div>

        @if (showSpecialStatusCheckboxes) {
        <div class="form-group full-width indent-field" formGroupName="specialStatusCheckboxes">
          <label class="sub-question-label">
            * If yes, select the status(es) that apply:
          </label>

          <div class="checkbox-container">
            <mat-checkbox formControlName="small">Small</mat-checkbox>
            <mat-checkbox formControlName="hpsa">HPSA (Health Professional Shortage Area)</mat-checkbox>
            <mat-checkbox formControlName="rural">Rural</mat-checkbox>
            <mat-checkbox formControlName="nonPatientFacing">Non-Patient Facing</mat-checkbox>
          </div>

          @if (
          mipsForm.get('specialStatusCheckboxes')?.invalid && (
          mipsForm.get('specialStatusCheckboxes')?.dirty ||
          mipsForm.get('specialStatusCheckboxes')?.touched ) ) {
          <mat-error class="error-message">
            Please select at least one status
          </mat-error>
          }
        </div>
        }

        <div id="break"></div>

        <div class="form-group full-width">
          <div class="question-container">
            <label class="question-label">
              * Will you be re-weighting any of the performance categories?
              (i.e., small practices, Extreme and Uncontrollable Circumstances (EUC) or Promoting Interoperability (PI) hardship)
              <a
                href="https://qpp.cms.gov/eligibility-participation/eligibility/exceptions"
                target="_blank"
                class="link"
              >For more information, you can view the exception application details here.</a>
            </label>

            <mat-radio-group
              formControlName="reweighting"
              class="inline-radio-group"
            >
              <mat-radio-button value="yes">Yes</mat-radio-button>
              <mat-radio-button value="no">No</mat-radio-button>
            </mat-radio-group>

            @if (isFieldInvalid('reweighting')) {
            <mat-error class="error-message">
              This field is required
            </mat-error>
            }
          </div>
        </div>

        @if (showReweightingCategories) {
        <div
          class="form-group full-width indent-field"
          formGroupName="reweightingCategories"
        >
          <label class="sub-question-label">
            * Select the approved categories from the checkboxes below:
          </label>

          <div class="checkbox-container">
            <mat-checkbox formControlName="quality">Quality</mat-checkbox>
            <mat-checkbox formControlName="pi">PI</mat-checkbox>
            <mat-checkbox formControlName="ia">IA</mat-checkbox>
            <mat-checkbox formControlName="cost">Cost</mat-checkbox>
          </div>

          @if (
          mipsForm.get('reweightingCategories')?.invalid && (
          mipsForm.get('reweightingCategories')?.dirty ||
          mipsForm.get('reweightingCategories')?.touched ) ) {
          <mat-error class="error-message">
            Please select at least one category
          </mat-error>
          }
        </div>
        }

        <div id="break"></div>

        <div class="form-group full-width">
          <div class="question-container">
            <label class="question-label">
              * Will you report data via any other third-party intermediary apart from Reg-ent registry?
            </label>

            <mat-radio-group
              formControlName="thirdPartyData"
              class="inline-radio-group"
            >
              <mat-radio-button value="yes">Yes</mat-radio-button>
              <mat-radio-button value="no">No</mat-radio-button>
            </mat-radio-group>

            @if (isFieldInvalid('thirdPartyData')) {
            <mat-error class="error-message">
              This field is required
            </mat-error>
            }
          </div>
        </div>

        @if (showThirdPartyDetails) {
        <div class="form-group two-unit indent-field">
          <mat-form-field appearance="outline" hideRequiredMarker>
            <mat-label>* If yes, provide details</mat-label>
            <input
              matInput
              formControlName="thirdPartyDataDetails"
              placeholder="Enter details"
            />

            @if (isFieldInvalid('thirdPartyDataDetails')) {
            <mat-error>
              @if (mipsForm.get('thirdPartyDataDetails')?.hasError('required')) {
                Please provide details
              }
              @if (mipsForm.get('thirdPartyDataDetails')?.hasError('maxlength')) {
                Maximum length is 512 characters
              }
            </mat-error>
            }
          </mat-form-field>
        </div>

        <div
          class="form-group full-width indent-field"
          formGroupName="thirdPartyDataCategories"
        >
          <label class="sub-question-label">
            * Select intended submission categories:
          </label>

          <div class="checkbox-container">
            <mat-checkbox formControlName="quality">Quality</mat-checkbox>
            <mat-checkbox formControlName="promotingInteroperability">
              Promoting Interoperability
            </mat-checkbox>
            <mat-checkbox formControlName="improvementActivities">
              Improvement Activities
            </mat-checkbox>
          </div>

          @if (
          mipsForm.get('thirdPartyDataCategories')?.invalid && (
          mipsForm.get('thirdPartyDataCategories')?.dirty ||
          mipsForm.get('thirdPartyDataCategories')?.touched ) ) {
          <mat-error class="error-message">
            Please select at least one category
          </mat-error>
          }
        </div>
        }
      </div>
    </form>
  </mat-card-content>
</mat-card>
